/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/customApplicationsApi.ts
 🗓️ Created: 2026-01-16
 🧩 Description:
    API functions for Custom Applications CRUD operations.

────────────────────────────────────────────────────────────────*/

import api from '../../../lib/api';
import {
    CustomApplication,
    CustomApplicationListResponse,
    CustomApplicationResponse,
    CreateCustomApplicationPayload,
    UpdateCustomApplicationPayload,
    DestinationModulesResponse
} from '../types';

const BASE_URL = '/api/telephony/custom-applications';

// Params type for list (exported for hook)
export interface ListCustomApplicationsParams {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    search?: string;
    appType?: 'lua' | 'xml';
    enabled?: boolean;
    domainId?: number;
}

// List all custom applications
export async function listCustomApplications(params?: ListCustomApplicationsParams): Promise<CustomApplicationListResponse> {
    const response = await api.get<CustomApplicationListResponse>(BASE_URL, { params });
    return response.data;
}

// Get single custom application by ID
export async function getCustomApplication(id: string | number): Promise<CustomApplicationResponse> {
    const response = await api.get<CustomApplicationResponse>(`${BASE_URL}/${id}`);
    return response.data;
}

// Create new custom application
export async function createCustomApplication(
    payload: CreateCustomApplicationPayload
): Promise<CustomApplicationResponse> {
    const response = await api.post<CustomApplicationResponse>(BASE_URL, payload);
    return response.data;
}

// Update existing custom application
export async function updateCustomApplication(
    id: string | number,
    payload: UpdateCustomApplicationPayload
): Promise<CustomApplicationResponse> {
    const response = await api.put<CustomApplicationResponse>(`${BASE_URL}/${id}`, payload);
    return response.data;
}

// Delete custom application
export async function deleteCustomApplication(
    id: string | number
): Promise<{ success: boolean; message: string }> {
    const response = await api.delete<{ success: boolean; message: string }>(`${BASE_URL}/${id}`);
    return response.data;
}

// Get available destination modules
export async function getDestinationModules(): Promise<DestinationModulesResponse> {
    const response = await api.get<DestinationModulesResponse>(`${BASE_URL}/destination-modules`);
    return response.data;
}
